import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import {
  addCivilCaseEvidence,
  editCivilCaseEvidence,
} from "services/civilCaseComplaintDetailService";
import { CivilCaseEvidence } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  evidence?: CivilCaseEvidence;
  complaintId: number | string;
  onClose: (refetch: boolean) => void;
};

const CivilCaseEvidenceForm: FC<Props> = ({
  isOpen,
  action,
  evidence,
  complaintId,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [description, setDescription] = useState("");
  const [attachment, setAttachment] = useState<File | null>();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (evidence) {
      setDescription(evidence?.description || "");
    }
  }, [evidence]);

  const validateForm = () => {
    if (!attachment && action === "Add") return "Attachment is Required.";
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction =
      action === "Edit" ? editCivilCaseEvidence : addCivilCaseEvidence;
    submitAction({
      id: action === "Edit" ? evidence?.id : undefined,
      description: description,
      evidence_attachment: attachment,
      complaint: complaintId,
    })
      .then(() => {
        showTooltip(
          action === "Edit"
            ? "Successfully Updated!"
            : "Successfully Submitted!",
          "success",
        );
        setDescription("");
        setAttachment(undefined);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => setLoading(false));
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null) => {
    setAttachment(file);
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white rounded-xl shadow-lg p-6 overflow-y-auto">
        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <div className="w-1 h-6 bg-red-600 rounded-full"></div>
          <h2 className="text-lg font-semibold text-gray-900">
            {action} Evidence
          </h2>
        </div>

        <form className="space-y-6" onSubmit={handleSubmit}>
          {/* Description */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-500">
              Description
            </label>
            <textarea
              rows={4}
              className="w-full p-3 bg-gray-50 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Enter a detailed description of the evidence..."
            />
          </div>

          {/* Attachment */}
          <div className="space-y-2">
            <FileUpload
              label="Attachment File"
              fileTypes="*"
              info="Upload evidence files (PDF, images, documents)"
              required={false}
              onChange={handleFile}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={() => onClose(false)}
              className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 mr-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2.5 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-all flex items-center shadow-sm hover:shadow"
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                `${action} Evidence`
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CivilCaseEvidenceForm;
